import Controller, { OnBeforeCancel, OnBeforeSubmit, OnMount, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { FormAPI, InputAPI, ToastService } from '@philips-internal/oswald-ui-components';
import BaseController from '../../../BaseController';
import { FragmentNames, ScoreMewsCreateAliases } from '../../../../codegen/index';
import moment from 'moment';
import { ISO_FULL_DATE_FORMAT } from '../../../../constants/date-utils';
import timeService from '../../../../services/time-service';
import { Inject} from '@odinjs/odin';
import InformationBarUtils from '../../../commons/header/information-bar-header-utils';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { Value } from '@philips-internal/oswald-ui-core';
import { LocatorTypes } from '../../../locator/locator-controller';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { Endpoint } from '../../../../constants/endpoints';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { HealthProfessionalUser } from '../../../../app/authentication';
import { DomainType, ResponseCreateScoreMEWS } from '../../score-types';
import DialogService from '../../../../services/dialog-service';
import { ScoreListContext } from '../../scores-list/scores-list-service';
import { ScoreType } from '../../scores-references/score-reference-controller';
import { AbortableCloseEvent } from '@philips-internal/oswald-ui-core/lib/component/types';

@Controller({ component: ScoreMewsCreateAliases.SCORE_MEWS_ADD_LAYOUT, fragment: FragmentNames.SCORE_MEWS_CREATE })
export default class ScoreMewsCreateController extends BaseController {

  @Inject(ScoreMewsCreateAliases.INPUT_SETUP_DATE)
  private setupDateInputApi!: InputAPI;

  @Inject(ScoreMewsCreateAliases.FORM_SCORE_MEWS_ADD)
  private formApi!: FormAPI;

  @OnMount({ target: ScoreMewsCreateAliases.FORM_SCORE_MEWS_ADD })
  handleFormMount(): void {
    ScoreListContext.currentTab = ScoreType.SCORE_MEWS
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    InformationBarUtils.collapsedBar = true;
    this.context.set({ encounterId, locator: LocatorTypes.PROFESSIONAL_PERSON, patientId });
    timeService.getCurrentDateTime()
      .then(date => this.setupDateInputApi.setValue(date))
      .catch(() => this.setupDateInputApi.setValue(moment().format(ISO_FULL_DATE_FORMAT)))
  }

  private showModalSubmit(idScore: number): void {
    DialogService.open({
      descriptionDialog: 'patient-evolution.release-title-evolution',
      onClickCancel: () => { this.goBack() },
      onClickOk: () => { this.releaseMEWScore(idScore) },
      titleDialog: 'patient-evolution.release-evolution',
      titleOnCancel: 'patient-evolution.release-cancel-evolution',
      titleOnOk: 'patient-evolution.release-submit-evolution',
    });
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'score-pews-create',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

  private goBack(): void {
    applicationContext.popNavigationItem();
    applicationContext.popNavigationItem();
  }

  private releaseMEWScore(sequenceNumber: number): void {
    this.formApi.setLoading(true);
    HttpService.put(Endpoint.RELEASE_SCORE_MEWS, { sequenceNumber })
      .then(() => {
        applicationContext.popNavigationItem();
      }).catch((err) => {
        this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
      }).finally(() => {
        this.formApi.setLoading(false)
      });
  }

  private createNewMEWSScore(body: Record<string,Value>): void {
    this.formApi.setLoading(true);
    HttpService.post(Endpoint.POST_CREATE_SCORE_MEWS, undefined, {body})
      .then(response => {
        const { idScoreIndexes } = response.body as ResponseCreateScoreMEWS;
        this.context.set({ idScoreIndexes, isCreated: true });
        this.showModalSubmit(idScoreIndexes);
      })
      .catch(err => {
        this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
      }).finally(() => this.formApi.setLoading(false));
  }

  @OnBeforeSubmit({ target: ScoreMewsCreateAliases.FORM_SCORE_MEWS_ADD })
  handleBeforeSubmit(): void {
    const isCreated = this.context.get('isCreated');
    const {encounterId, idScoreIndexes} = this.historyService.getCurrentRouteParams();
    if (isCreated) {
      this.showModalSubmit(idScoreIndexes as number);
      return;
    }
    const formData = this.formApi.getRecord();
    const levelConsciousness = formData.consciousnessLevel as DomainType;
    const paSistolica = formData.paSistolica ? formData.paSistolica : '';
    const temperature = formData.temperatura ? formData.temperatura : '';
    const heartRate = formData.freqCardiaca ? formData.freqCardiaca : '';
    const respiratoryRate = formData.freqRespiratoria ? formData.freqRespiratoria : '';
    const user = foundationContext.getUser() as unknown as HealthProfessionalUser;
    const evaluationDate = moment(formData.dateEvaluation as string).utc().format(ISO_FULL_DATE_FORMAT);

    const body = {
      attendance: encounterId as number,
      evaluationDate,
      freqCardiaca: heartRate,
      freqRespiratoria: respiratoryRate,
      idScoreIndexes: null,
      nivelConsciencia: levelConsciousness ? levelConsciousness.valueDomain : '',
      paSistolica,
      professionalCode: user.physicalPersonId,
      temperatura: temperature,
    }
    this.createNewMEWSScore(body);
  }

  private discardChanges(): void {
    DialogService.open({
      descriptionDialog: 'clinical-notes.discard-changes',
      onClickCancel: () => {},
      onClickOk: () => {this.goBack();},
      titleDialog: 'clinical-notes.discard-changes',
      titleOnCancel: 'patient-evolution.cancel',
      titleOnOk: 'clinical-notes.discard-submit'
    });
  }

  @OnUnmount(ScoreMewsCreateAliases.SCORE_MEWS_ADD_LAYOUT)
  handleUnmount(): void {
    ScoreListContext.clean();
    ScoreListContext.currentTab = ScoreType.SCORE_MEWS;
  }

  @OnBeforeCancel(ScoreMewsCreateAliases.FORM_SCORE_MEWS_ADD)
  onCancelForm(event: AbortableCloseEvent): void {
    event.abort();
    this.discardChanges();
  }
}
